import { SendMoneyPostValidationParameter } from "../view-models/SendMoneyPostValidationParameter";
import { useSessionStore } from "../../../state/src/useSessionStore";
import { ConsumerRegistration } from "../view-models/ConsumerRegistration";
import { ItemChoiceType } from "../view-models/itemChoiceType";

export const buildSendValidationRequestFromStore = (consumerId: string | number): SendMoneyPostValidationParameter => {
	const state = useSessionStore.getState() as any;

	const request = new SendMoneyPostValidationParameter();

	// BasicRequest fields
	request.agentID = state.agentId || undefined;
	request.unitProfileID = state.unitProfileId || undefined;

	// Core transaction amounts/currencies/options
	const opt = state.selectedSendOption || state.pendingSendOption || {};
	const amountFromState = Number(String(state.currentSendAmount || "0").replace(/,/g, ""));
	const amountFromOption = Number(opt?.SendAmounts?.SendAmount ?? 0);
	request.Amount = isFinite(amountFromOption) && amountFromOption > 0 ? amountFromOption : (isFinite(amountFromState) ? amountFromState : undefined);
	request.SendCurrency = opt?.SendAmounts?.SendCurrency || "USD";
	request.DestinationCountry = state.selectedDestinationCountryCode  || undefined;
	if(request.DestinationCountry?.toUpperCase() === "USA" || request.DestinationCountry?.toUpperCase() === "CAN") {
		request.DestinationState = state.selectedDestinationStateAbbr || undefined;
	}
	request.DeliveryOption = opt?.DeliveryOption || undefined;
	request.ReceiveCurrency = state.selectedReceiveCurrency || opt?.ValidReceiveCurrency || opt?.EstimatedReceiveCurrency || undefined;
	request.ReceiveAgentId = opt?.ReceiveAgentId ?? opt?.receiveAgentID ?? opt?.ReceiveAgentID ?? undefined;

	// Receipt language preferences
	request.PrimaryReceiptLanguage = "ENG";
	request.SecondaryReceiptLanguage = "SPA";

	// Consumer linkage
	request.ConsumerId = consumerId != null ? String(consumerId) : undefined;

	// MGI session id from selected service options or store fallback
	request.MgiTransactionSessionId = opt?.MgiTransactionSessionId || opt?.mgiTransactionSessionId || state.mgiTransactionSessionId || undefined;

	// Sender info from store
	request.SenderFirstName = state.senderFirstName || undefined;
	request.SenderMiddleName = state.senderMiddleName || undefined;
	request.SenderLastName = state.senderLastName || undefined;
	request.SenderLastName2 = state.senderLastName2 || undefined;
	request.SenderAddress = state.senderAddress || undefined;
	request.SenderCity = state.senderCity || undefined;
	request.SenderCountry = 'USA';
	request.FormFreeStaging = true;
	request.SenderZipCode = state.senderZipCode || undefined;
	request.SenderState = state.senderState || undefined;
	request.SenderDOB = state.senderDOB || undefined;
	// Fallback: parse senderDOBString (MM/DD/YYYY)
	if (!request.SenderDOB && state.senderDOBString) {
		try {
			const parts = String(state.senderDOBString).split("/");
			if (parts.length === 3) {
				const mm = parseInt(parts[0], 10);
				const dd = parseInt(parts[1], 10);
				const yyyy = parseInt(parts[2], 10);
				const d = new Date(yyyy, mm - 1, dd);
				if (!isNaN(d.getTime())) {
					request.SenderDOB = d;
				}
			}
		} catch {}
	}
	request.SenderPhotoIdNumber = state.senderPhotoIdNumber || undefined;
	request.SenderPhotoIdCountry = state.selectedIdPhotoIdCountry || undefined;
	// Optional: ID type if chosen (store uses selectedIdTypeValue)
	request.SenderPhotoIdType = state.selectedIdTypeValue || undefined;
	request.SenderPhotoIdState = state.selectedIdPhotoIdStateAbbr || undefined;
	request.SenderHomePhone = state.phoneNumber || undefined;

	// Receiver info from store (captured in ReceiverFullNameStep)
	request.ReceiverFirstName = state.receiverFirstName || undefined;
	request.ReceiverMiddleName = state.receiverMiddleName || undefined;
	request.ReceiverLastName = state.receiverLastName || undefined;
	request.ReceiverLastName2 = state.receiverLastName2 || undefined;

	// If CustomerReceiveNumber captured in flow, include it
	request.CustomerReceiveNumber = state.customerReceiveNumber || undefined;

	// Include promo codes from the most recent FeeLookup params stored in session
	try {
		const promoCodes: string[] | undefined = state?.sendFeeLookupReviewEditStore?.promoCodes;
		if (Array.isArray(promoCodes) && promoCodes.length > 0) {
			request.PromoCodeValues = promoCodes.filter((p) => typeof p === 'string' && p.trim().length > 0);
		}
	} catch {}

	// ConsumerRegistration info: phone number and PIN
	const phone = state.phoneNumber || undefined;
	request.SenderHomePhone = phone;
	const pin = (() => {
		// Use a single canonical store field for PIN
		const p = state.consumerPin ?? state.pin ?? state.PIN ?? undefined;
		return p !== undefined && p !== null ? String(p) : undefined;
	})();
	if (phone || pin) {
		const reg = new ConsumerRegistration();
		// Preferred shape for API
		reg.consumerLoginID = phone;
		reg.consumerPIN = pin ;
		request.ConsumerRegistration = reg;
	}

    // Merge additional information fields (case-insensitive)
    try {
        const extras: Record<string, any> = state.additionalFieldValues || {};
        for (const key of Object.keys(extras)) {
            const value = extras[key];
            // prefer existing exact match; otherwise map case-insensitively
            const existingKeys = Object.keys(request as any);
            const exact = existingKeys.find(k => k === key);
            if (exact) {
                (request as any)[exact] = value;
                continue;
            }
            const ci = existingKeys.find(k => k.toLowerCase() === key.toLowerCase());
            if (ci) {
                (request as any)[ci] = value;
            } else {
                // if unknown, add as-is
                (request as any)[key] = value;
            }
        }
    } catch {}

    return request;
};
